import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Copy, Check, Mail, Share2, MoreHorizontal } from 'lucide-react';
import { FaXTwitter, FaFacebook, FaReddit, FaWhatsapp, FaLinkedinIn } from 'react-icons/fa6';
import { buildShareText } from '../../utils/pageMeta';
import './ShareModal.css';
import '../Modals/Modals.css';

const buildPlatforms = (shareUrl, locationName) => {
  const { title, body } = buildShareText(locationName);

  return [
    {
      id: 'email',
      label: 'Email',
      icon: Mail,
      href: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${body}:\n\n${shareUrl}`)}`,
    },
    {
      id: 'x',
      label: 'X',
      icon: FaXTwitter,
      href: `https://x.com/intent/tweet?text=${encodeURIComponent(body)}&url=${encodeURIComponent(shareUrl)}`,
    },
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      icon: FaWhatsapp,
      href: `https://wa.me/?text=${encodeURIComponent(`${title}\n${shareUrl}`)}`,
    },
    {
      id: 'facebook',
      label: 'Facebook',
      icon: FaFacebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    },
    {
      id: 'reddit',
      label: 'Reddit',
      icon: FaReddit,
      href: `https://www.reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(title)}`,
    },
    {
      id: 'linkedin',
      label: 'LinkedIn',
      icon: FaLinkedinIn,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    },
  ];
};

const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

const ShareModal = ({ isOpen, onClose, shareUrl, locationName }) => {
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  const platforms = buildPlatforms(shareUrl, locationName);
  const { title, body } = buildShareText(locationName);

  useEffect(() => {
    if (!isOpen) {
      setCopied(false);
      return;
    }

    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscape = (e) => {
      if (e.key !== 'Escape') return;
      onClose();
      setTimeout(() => document.activeElement?.blur(), 0);
    };
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', handleEscape);
      clearTimeout(timerRef.current);
    };
  }, [isOpen, onClose]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      inputRef.current?.select();
      document.execCommand('copy');
    }
    setCopied(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 2000);
  }, [shareUrl]);

  const handleNativeShare = async () => {
    try {
      await navigator.share({ title, text: body, url: shareUrl });
    } catch {
      // AbortError = user dismissed — that's fine
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="modal-overlay share-modal-overlay"
      onClick={onClose}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <div
        className="share-modal"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 className="share-modal-title">
            <Share2 size={18} />
            Share
          </h2>
          <button className="close-button" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div className="share-section-label">Copy link</div>
          <div className="share-url-row">
            <input
              ref={inputRef}
              className="share-url-input"
              type="text"
              value={shareUrl}
              readOnly
              onFocus={(e) => e.target.select()}
              aria-label="Share URL"
            />
            <button
              className={`share-copy-button ${copied ? 'copied' : ''}`}
              onClick={handleCopy}
              aria-label={copied ? 'Copied' : 'Copy link'}
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              <span>{copied ? 'Copied!' : 'Copy'}</span>
            </button>
          </div>

          <div className="share-section-label share-via-label">Share via</div>
          <div className="share-platforms">
            {platforms.map((platform) => {
              const Icon = platform.icon;
              return (
                <a
                  key={platform.id}
                  className="share-platform-button"
                  href={platform.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Share on ${platform.label}`}
                >
                  <div className={`share-platform-icon share-platform-icon--${platform.id}`}>
                    <Icon size={20} />
                  </div>
                  <span className="share-platform-label">{platform.label}</span>
                </a>
              );
            })}
            {canNativeShare && (
              <button
                className="share-platform-button"
                onClick={handleNativeShare}
                aria-label="More share options"
              >
                <div className="share-platform-icon">
                  <MoreHorizontal size={20} />
                </div>
                <span className="share-platform-label">More</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ShareModal;
